import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Breadcrumb } from '@/components/Breadcrumb'
import { StyledActionButton } from '@/components/Buttons'
import { Checkbox } from '@/components/Checkbox'
import { FormStyled, FormSubmit } from '@/components/templates/Form'
import { ScrollableContent } from '@/components/templates/ScreenLayout'
import { ScreenWrapper } from '@/components/templates/ScreenWrapper'
import { Subheader } from '@/components/Text'
import { useTrackFormProgress } from '@/hooks/useTrackFormProgress'
import {
  patientDetailsPath,
  selectClinicPath,
  selectDoctorPath,
} from '@/router/paths'
import { VelthuisFormStep } from '@/utils/VelthuisFormSteps'

import * as S from './HealthWarning.styled'

export const HealthWarning = () => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'healthWarning',
  })
  const navigate = useNavigate()
  const [isAcknowledged, setIsAcknowledged] = useState(false)

  const { appointmentType, category, doctorId, locationId } = useParams()

  const { trackFormInteraction } = useTrackFormProgress(
    VelthuisFormStep.HealthWarning,
  )

  const handleCheckboxChange = (checked: boolean) => {
    setIsAcknowledged(checked)
    trackFormInteraction({
      action: 'click',
      formFieldName: 'health-warning-acknowledgement',
      formFieldValue: checked.toString(),
    })
  }

  const handleSubmit = (e: any) => {
    e.preventDefault()

    if (isAcknowledged && category && appointmentType) {
      trackFormInteraction({
        action: 'click',
        formFieldName: 'health-warning-continue',
      })
      if (locationId && doctorId) {
        navigate(patientDetailsPath())
      } else if (locationId && !doctorId) {
        navigate(
          selectDoctorPath({
            appointmentType,
            category,
            locationId,
          }),
        )
      } else {
        navigate(
          selectClinicPath({
            appointmentType,
            category,
          }),
        )
      }
    }
  }

  return (
    <ScreenWrapper>
      <Breadcrumb
        onClick={() => {
          navigate(-1)
          trackFormInteraction({
            action: 'previous',
            formFieldName: 'back-to-previous',
          })
        }}
        title={t('breadcrumbLink')}
      />

      <FormStyled method="post" onSubmit={handleSubmit}>
        <ScrollableContent>
          <S.ContentWrapper>
            <S.AlertIconWrapper>
              <S.AlertIcon />
            </S.AlertIconWrapper>

            <S.TextContent>
              <Subheader>{t('header')}</Subheader>

              <S.BulletList>
                <S.BulletItem>{t('conditions.epilepsy')}</S.BulletItem>
                <S.BulletItem>{t('conditions.heartProblems')}</S.BulletItem>
                <S.BulletItem>
                  {t('conditions.neurologicalIssues')}
                </S.BulletItem>
                <S.BulletItem>{t('conditions.lungProblems')}</S.BulletItem>
                <S.BulletItem>{t('conditions.smoking')}</S.BulletItem>
              </S.BulletList>

              <S.Paragraph>{t('description')}</S.Paragraph>

              <S.CheckboxContainer>
                <Checkbox
                  id="health-acknowledgement"
                  name="health-acknowledgement"
                  checked={isAcknowledged}
                  onChange={handleCheckboxChange}
                  label={t('acknowledgementLabel')}
                />
              </S.CheckboxContainer>
            </S.TextContent>
          </S.ContentWrapper>
        </ScrollableContent>

        <FormSubmit>
          <StyledActionButton
            variant="Primary"
            type="submit"
            disabled={!isAcknowledged}
          >
            {t('actions.continue')}
          </StyledActionButton>
        </FormSubmit>
      </FormStyled>
    </ScreenWrapper>
  )
}
